import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, useLocation, Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Check, Gift } from "lucide-react";
import { PhoneAuthInput } from "@/components/connect/PhoneAuthInput";
import { OtpVerifyInput } from "@/components/connect/OtpVerifyInput";
import { TermsDialog } from "@/components/connect/TermsDialog";
import { PrivacyDialog } from "@/components/connect/PrivacyDialog";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { useTranslation } from "react-i18next";
import useNativeStatusBar from "@/hooks/useNativeStatusBar";
import { getTopSafeAreaStyle, useSafeArea } from "@/hooks/useSafeArea";
import { buildPublicUrl } from "@/lib/public-url";
import { useAuth } from "@/contexts/AuthContext";
import elefaAppIcon from "@/assets/elefa-app-icon.webp";
import { ElefaLoadingScreen } from "@/components/connect/ElefaLoadingScreen";
import { getDeviceFingerprint } from "@/lib/deviceFingerprint";
import { WelcomeCreditCard } from "@/components/connect/WelcomeCreditCard";
import { stripTrunkZero } from "@/lib/domestic-number-detection";

type AuthStep = "phone_input" | "otp_verify" | "success";



// Phone-number prefill is intentionally disabled. The previous progress-
// persistence layer (localStorage + Capacitor Preferences) caused stale
// numbers (e.g. "616…" / last-typed) to re-appear on a fresh signup
// screen. The auth page now always starts blank, every time.
const LEGACY_OTP_PROGRESS_KEYS = [
  'elefa.connect.auth.progress',
  'elefa.connect.auth.progress.native',
];
const clearLegacyProgress = () => {
  try {
    for (const k of LEGACY_OTP_PROGRESS_KEYS) localStorage.removeItem(k);
    sessionStorage.removeItem('elefa.connect.auth.progress');
  } catch { /* ignore */ }
  // Also clear any native Capacitor Preferences entry (best-effort).
  void (async () => {
    try {
      const { Preferences } = await import('@capacitor/preferences');
      for (const k of LEGACY_OTP_PROGRESS_KEYS) {
        await Preferences.remove({ key: k });
      }
    } catch { /* native storage unavailable on web */ }
  })();
};

export default function ConnectAuthPage() {
  const { t } = useTranslation("portal");
  const { t: tw } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, isLoading: authLoading } = useAuth();
  const [step, setStep] = useState<AuthStep>("phone_input");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isNewAccount, setIsNewAccount] = useState(false);
  const [keyboardInset, setKeyboardInset] = useState(0);
  const [lastChannel, setLastChannel] = useState<'sms' | 'whatsapp'>('sms');
  const safeAreaInsets = useSafeArea();
  const topSafeAreaStyle = getTopSafeAreaStyle(safeAreaInsets);

  // Ensure the native status bar is visible with dark icons on the white auth
  // background, and is NOT overlaying the WebView (so the time, signal and
  // battery indicators stay clearly readable above the logo).
  useNativeStatusBar('light');

  const headerLogo = (
    <img
      src={elefaAppIcon}
      alt=""
      width={56}
      height={56}
      decoding="sync"
      loading="eager"
      className="h-12 w-12 rounded-[22%] object-cover shadow-sm-soft"
    />
  );

  // Track soft-keyboard height via visualViewport (works in WKWebView + mobile
  // browsers without requiring @capacitor/keyboard). Keeps the CTA above the
  // keyboard when the user focuses the phone or OTP field.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return;
    const vv = window.visualViewport;
    const update = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardInset(inset);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  // Unified auth (Boss Revolution / Talk360 pattern). Path is preserved for
  // backwards compatibility (/login, /signup, /auth all render this same UI),
  // but the server resolves login vs signup automatically via authMode 'auto'.
  const redirectTo = searchParams.get('redirect') || '/connect-portal/dashboard';
  const referralCode = searchParams.get('ref') || '';

  const stepIndex = step === 'phone_input' ? 0 : step === 'otp_verify' ? 1 : 2;

  // Phone-number prefill is intentionally OFF. Wipe any legacy stored
  // progress so a stale number from an earlier build (the "616…" /
  // last-typed prefill bug) can never re-appear.
  useEffect(() => {
    clearLegacyProgress();
  }, []);




  const handleSendOtp = async (rawPhoneNumber: string, channel: 'sms' | 'whatsapp' = 'sms') => {
    setIsLoading(true);
    // Normalize: strip embedded trunk zero so users who pick +61 and then type
    // "0450 294 281" don't end up with "+610450294281" (Twilio rejects it and
    // the OTP screen shows the wrong number). Server re-normalizes too.
    const digits = rawPhoneNumber.replace(/\D/g, '');
    const fullPhoneNumber = digits ? `+${stripTrunkZero(digits)}` : rawPhoneNumber;
    setPhoneNumber(fullPhoneNumber);
    setLastChannel(channel);

    try {
      const { data, error } = await supabase.functions.invoke<{ error?: string; code?: string }>(
        'twilio-verify-send',
        { body: {
          phone: fullPhoneNumber,
          authMode: 'auto',
          channel,
          referralCode: referralCode || undefined,
        } },
      );

      if (error) {
        console.error("Supabase SDK error:", error);
        toast.error(tw("web.auth.genericError"));
        return;
      }

      if (data?.error || data?.code) {
        if (data.code === 'RATE_LIMITED' || data.code === 'TWILIO_RATE_LIMITED') {
          toast.error(tw("web.auth.rateLimited"));
          return;
        }
        if (data.code === 'INVALID_PHONE') {
          toast.error(tw("web.auth.invalidPhone"));
          return;
        }
        if (data.code === 'SERVER_ERROR') {
          toast.error(tw("web.auth.serverError"));
          return;
        }
        if (data.error) {
          toast.error(data.error);
          return;
        }
      }

      toast.success(tw("web.auth.codeSent"));
      // prefill disabled - no progress persistence
      setStep("otp_verify");
    } catch (error: unknown) {
      console.error("OTP send error:", error);
      toast.error(tw("web.auth.genericError"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (code: string) => {
    if (isComplete || isVerifying) return;
    setIsVerifying(true);

    try {
      const deviceFp = await getDeviceFingerprint().catch(() => '');
      const { data, error } = await supabase.functions.invoke<{
        error?: string;
        code?: string;
        success?: boolean;
        phoneVerified?: boolean;
        isNewAccount?: boolean;
        session?: { access_token?: string; refresh_token?: string };
        hashed_token?: string;
      }>('twilio-verify-check', {
        body: {
          phone: phoneNumber,
          code,
          authMode: 'auto',
          referralCode: referralCode || undefined,
          redirectTo: buildPublicUrl(redirectTo),
        },
        headers: deviceFp ? { 'x-device-fingerprint': deviceFp } : undefined,
      });

      if (error) {
        console.error("Supabase SDK error:", error);
        toast.error(tw("web.auth.genericError"));
        setIsVerifying(false);
        return;
      }

      if (data?.error) {
        if (data.code === 'SERVER_ERROR') {
          toast.error(tw("web.auth.serverErrorShort"), { description: tw("web.auth.tryAgainMoment") });
          setIsVerifying(false);
          return;
        }
        if (data.code === 'TWILIO_ERROR' || data.code === 'INVALID_CODE') {
          if (data.error.includes("expired") || data.error.includes("not found")) {
            toast.error(tw("web.auth.codeExpired"));
          } else if (data.error.includes("Maximum")) {
            toast.error(tw("web.auth.tooManyRequestNew"));
          } else {
            toast.error(tw("web.auth.invalidCode"));
          }
          setIsVerifying(false);
          return;
        }
        if (data.error.includes("expired")) {
          toast.error(tw("web.auth.codeExpired"));
        } else if (data.error.includes("Invalid")) {
          toast.error(tw("web.auth.invalidCode"));
        } else {
          toast.error(data.error);
        }
        setIsVerifying(false);
        return;
      }

      if (data?.success && data?.phoneVerified) {
        setIsComplete(true);
        setIsNewAccount(!!data.isNewAccount);
        setStep("success");
        clearLegacyProgress();

        confetti({ particleCount: 80, spread: 60, origin: { y: 0.6 } });

        // Returning users navigate straight in; new accounts stay on the
        // success step so the WelcomeCreditCard (real balance + real minutes)
        // gets a chance to render before the dashboard takes over.
        const goNext = () => {
          if (data.isNewAccount) return; // stay on success step
          navigate(redirectTo, { replace: true });
        };

        if (data.session?.access_token && data.session?.refresh_token) {
          const { error: sessionErr } = await supabase.auth.setSession({
            access_token: data.session.access_token,
            refresh_token: data.session.refresh_token,
          });
          if (!sessionErr) {
            goNext();
            return;
          }
          console.error('setSession error after OTP verification:', sessionErr);
        }

        if (data.hashed_token) {
          try {
            const { error: verifyErr } = await supabase.auth.verifyOtp({
              token_hash: data.hashed_token,
              type: 'magiclink',
            });
            if (!verifyErr) {
              goNext();
              return;
            }
            console.error('verifyOtp error after OTP verification:', verifyErr);
          } catch (e) {
            console.error('verifyOtp threw after OTP verification:', e);
          }
        }

        toast.error(tw("web.auth.verifiedButFailed"));
        setIsComplete(false);
        setStep("otp_verify");
        setIsVerifying(false);
      }
    } catch (error: unknown) {
      console.error("OTP verify error:", error);
      toast.error(tw("web.auth.genericError"));
      setIsVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    setIsResending(true);
    try {
      const { data, error } = await supabase.functions.invoke<{ error?: string }>(
        'twilio-verify-send',
        { body: { phone: phoneNumber, authMode: 'auto', channel: lastChannel } },
      );
      if (error) {
        console.error("Resend SDK error:", error);
        toast.error(tw("web.auth.genericError"));
        return;
      }
      if (data?.error) { toast.error(data.error); return; }
      toast.success(lastChannel === 'whatsapp' ? tw("web.auth.newCodeWhatsapp") : tw("web.auth.newCodeSent"));
    } catch (error: unknown) {
      console.error("Resend OTP error:", error);
      toast.error(tw("web.auth.genericError"));
    } finally {
      setIsResending(false);
    }
  };

  const handleBack = () => {
    clearLegacyProgress();
    setStep("phone_input");
    setPhoneNumber("");
  };

  // ---- Cold-launch render gate ---------------------------------------------
  // Prevents the "unauth UI flashes before the user is signed in" glitch on
  // app resume. While the AuthProvider is still resolving the cached session
  // we render a neutral splash; if the resolved session has a user we redirect
  // to the post-login destination instead of showing the phone-input form.
  if (authLoading) {
    return (
      <div className="absolute inset-0 bg-background" aria-hidden style={topSafeAreaStyle}>
        <ElefaLoadingScreen fullScreen hideLabel />
      </div>
    );
  }
  if (user) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    // Native-stable: absolute fill (mirrors ConnectDialer) - avoids dvh quirks
    // in Capacitor WKWebView and lets safe-area utilities reserve the status bar.
    <div className="absolute inset-0 flex flex-col bg-background">
      {/* Sticky header - respects iOS safe area (Dynamic Island / notch) */}
      <div className="flex-none z-30 bg-background/95 backdrop-blur-md" style={topSafeAreaStyle}>
        <div className="container py-3 flex items-center justify-center">
          {headerLogo}
        </div>
      </div>

      {/* Scrollable content area (keyboard-safe via visualViewport) */}
      <div
        className="flex-1 min-h-0 overflow-y-auto transition-[padding] duration-150 ease-out"
        style={{ paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + 1rem + ${keyboardInset}px)` }}
      >
        {/* Progress dots */}
        <div className="flex justify-center gap-2 mt-3 mb-4">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === stepIndex ? 'w-8 bg-primary' : i < stepIndex ? 'w-2 bg-primary/60' : 'w-2 bg-muted'
              }`}
            />
          ))}
        </div>

        <div className="flex items-start justify-center px-4 pt-1">
          <div className="w-full max-w-md">
            <AnimatePresence mode="wait">
              {step === "success" ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className={isNewAccount ? "pt-2" : "text-center py-12"}
                >
                  {isNewAccount ? (
                    <WelcomeCreditCard firstName={user?.user_metadata?.first_name as string | undefined} />
                  ) : (
                    <>
                      <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                        <Check className="h-10 w-10 text-primary" />
                      </div>
                      <h1 className="text-3xl font-bold text-foreground mb-2">{t("youreIn")}</h1>
                      <p className="text-muted-foreground">{t("takingToDashboard")}</p>
                    </>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Heading - only shown on phone_input. OtpVerifyInput renders its own
                      header (with masked number) so we don't duplicate it on otp_verify. */}
                  {step === "phone_input" && (
                    <div className="text-center mb-6">
                      <h1 className="text-3xl font-bold text-foreground mb-2">
                        {tw("web.auth.heading")}
                      </h1>
                      <p className="text-muted-foreground">
                        {tw("web.auth.subheading")}
                      </p>
                    </div>
                  )}

                  <div className="rounded-[2rem] bg-card p-5 shadow-card border border-border/40">
                    {step === "phone_input" ? (
                      <>
                        <PhoneAuthInput
                          onSubmit={handleSendOtp}
                          isLoading={isLoading}
                          mode="login"
                          buttonText={tw("web.auth.sendCode")}
                          initialPhone={phoneNumber}
                          onPhoneInputChange={setPhoneNumber}
                        />
                        <div className="flex items-start gap-2 p-3 mt-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200/60 dark:border-emerald-900/40">
                          <Gift className="h-4 w-4 text-emerald-700 dark:text-emerald-400 mt-0.5 shrink-0" />
                          <span className="text-sm text-emerald-900 dark:text-emerald-200">
                            {tw("web.auth.welcomeCredit")}
                          </span>
                        </div>
                        {referralCode && (
                          <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-xl mt-4">
                            <Check className="h-4 w-4 text-primary" />
                            <span className="text-sm">
                              {t("referralCodeApplied", { code: referralCode })}
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <OtpVerifyInput
                        phoneNumber={phoneNumber}
                        onVerify={handleVerifyOtp}
                        onResend={handleResendOtp}
                        onBack={handleBack}
                        isVerifying={isVerifying}
                        isResending={isResending}
                        isComplete={isComplete}
                      />
                    )}
                  </div>

                  {/* Terms & Privacy always visible - covers signup disclosure for new users
                      without needing a separate signup screen */}
                  <div className="mt-6 text-center text-xs text-muted-foreground">
                    {t("byContinuing")}{" "}
                    <TermsDialog
                      trigger={<span className="text-primary hover:underline cursor-pointer">{t("terms")}</span>}
                    />
                    {" "}{t("and")}{" "}
                    <PrivacyDialog
                      trigger={<span className="text-primary hover:underline cursor-pointer">{t("privacyPolicyShort")}</span>}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
